
class ImageCacheService {
  private cache = new Map<string, Promise<boolean>>();
  private loadedImages = new Set<string>();
  private failedImages = new Set<string>();
  private preloadQueue: string[] = [];
  private isProcessingQueue = false;
  private cacheVersion = Date.now(); // Cache versioning to prevent stale content

  async preloadImage(src: string): Promise<boolean> {
    if (this.loadedImages.has(src)) {
      return true;
    }

    if (this.failedImages.has(src)) {
      return false;
    }

    if (this.cache.has(src)) {
      return this.cache.get(src)!;
    }

    const loadPromise = new Promise<boolean>((resolve) => {
      const img = new Image();
      
      // Set timeout for slow loading images  
      const timeout = setTimeout(() => {
        this.cache.delete(src);
        this.failedImages.add(src);
        resolve(false);
      }, 5000); // Reduced timeout to 5 seconds
      
      img.onload = () => {
        clearTimeout(timeout);
        this.loadedImages.add(src);
        resolve(true);
      };
      
      img.onerror = () => {
        clearTimeout(timeout);
        this.cache.delete(src);
        this.failedImages.add(src);
        resolve(false);
      };
      
      // Use cross-origin anonymous to enable caching
      img.crossOrigin = 'anonymous';
      img.src = src;
    });

    this.cache.set(src, loadPromise);
    return loadPromise;
  }

  // Batch preload images with priority queue
  async preloadImages(srcs: string[], priority = false): Promise<boolean[]> {
    if (priority) {
      // Process high priority images immediately
      return Promise.all(srcs.map(src => this.preloadImage(src)));
    }

    // Add to queue for lower priority images
    this.preloadQueue.push(...srcs.filter(src => !this.cache.has(src)));
    this.processQueue();
    
    return Promise.all(srcs.map(src => this.preloadImage(src)));
  }

  private async processQueue() {
    if (this.isProcessingQueue || this.preloadQueue.length === 0) return;
    
    this.isProcessingQueue = true;
    
    // Process 3 images at a time to avoid overwhelming the browser
    while (this.preloadQueue.length > 0) {
      const batch = this.preloadQueue.splice(0, 3);
      await Promise.all(batch.map(src => this.preloadImage(src)));
      
      // Small delay between batches to prevent blocking
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    this.isProcessingQueue = false;
  }

  isImageLoaded(src: string): boolean {
    return this.loadedImages.has(src);
  }

  hasImageFailed(src: string): boolean {
    return this.failedImages.has(src);
  }

  clearCache(): void {
    this.cache.clear();
    this.loadedImages.clear();
    this.failedImages.clear();
    this.preloadQueue = [];
    this.cacheVersion = Date.now(); // Update cache version
  }

  // Clear specific image from cache and browser cache
  clearImage(src: string): void {
    this.cache.delete(src);
    this.loadedImages.delete(src);
    this.failedImages.delete(src);
    
    // Force browser to reload by creating versioned URL
    const versionedSrc = this.getVersionedUrl(src);
    if (versionedSrc !== src) {
      this.cache.delete(versionedSrc);
      this.loadedImages.delete(versionedSrc);
      this.failedImages.delete(versionedSrc);
    }
  }

  // Force clear all cache including browser cache
  clearAllCache(): void {
    this.clearCache();
    // Increment cache version to invalidate all cached images
    this.cacheVersion = Date.now();
  }

  // Get versioned URL to prevent browser cache issues
  private getVersionedUrl(src: string): string {
    if (!src) return src;
    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}_v=${this.cacheVersion}`;
  }

  // Validate that image is actually loaded and not stale
  isImageValidAndLoaded(src: string): boolean {
    return this.loadedImages.has(src) && !this.failedImages.has(src);
  }

  getCacheSize(): number {
    return this.loadedImages.size;
  }

  getFailedCount(): number {
    return this.failedImages.size;
  }
}

export const imageCache = new ImageCacheService();
